import React, { useEffect, useMemo } from "react";

import { Grid } from "@mui/material";
import { isBefore } from "date-fns";
import { format } from "date-fns-tz";
import { MemberTimesheet } from "Models/members/member.timesheet";
import { useTranslation } from "react-i18next";

import { Input } from "../../../../../../../../../../../../components/Form";
import useYupValidation from "../../../../../../../../../../../../hooks/useYupValidation";
import { useActions } from "../../../../../hooks/actions";
import { TimesheetFormWrapper } from "./styles";

type TimesheetFormProps = {
  isLoading: boolean;
  isOnView?: boolean;
};

const DATE_FORMAT = "yyyy-MM-dd'T'HH:mm";

const getDateTimeLabelText = (value?: string | Date | undefined): string => {
  return value ? format(new Date(value), DATE_FORMAT) : "";
};

export const TimesheetForm: React.FC<TimesheetFormProps> = (props) => {
  const { isOnView, isLoading } = props;
  const { t: translate } = useTranslation();
  const { timesheets = {}, setTimesheets, timesheetErrors } = useActions();
  const { fieldHasError, getErrorByField } = useYupValidation(timesheetErrors);

  const returnDateFormation = (newDatetime: string) => {
    const date = new Date(newDatetime);

    return date;
  };

  useEffect(() => {
    if (!timesheets) return;
    const updatedTimesheets: {
      arrivedAtTheResidence?: string | Date | undefined;
      leavingTheResidence?: string | Date | undefined;
      arrivedBackToTheWarehouse?: string | Date | undefined;
      breakFrom?: string | Date | undefined;
      backFromBreak?: string | Date | undefined;
    } = {};

    if (
      timesheets.arrivedAtTheResidence &&
      timesheets.breakFrom &&
      isBefore(new Date(timesheets.breakFrom), new Date(timesheets.arrivedAtTheResidence))
    ) {
      updatedTimesheets.breakFrom = undefined;
      updatedTimesheets.backFromBreak = undefined;
      updatedTimesheets.leavingTheResidence = undefined;
      updatedTimesheets.arrivedBackToTheWarehouse = undefined;
    }

    if (
      timesheets.breakFrom &&
      timesheets.backFromBreak &&
      isBefore(new Date(timesheets.backFromBreak), new Date(timesheets.breakFrom))
    ) {
      updatedTimesheets.backFromBreak = undefined;
      updatedTimesheets.leavingTheResidence = undefined;
      updatedTimesheets.arrivedBackToTheWarehouse = undefined;
    }

    if (
      timesheets.backFromBreak &&
      timesheets.leavingTheResidence &&
      isBefore(new Date(timesheets.leavingTheResidence), new Date(timesheets.backFromBreak))
    ) {
      updatedTimesheets.leavingTheResidence = undefined;
      updatedTimesheets.arrivedBackToTheWarehouse = undefined;
    }

    if (
      timesheets.arrivedAtTheResidence &&
      timesheets.leavingTheResidence &&
      isBefore(new Date(timesheets.leavingTheResidence), new Date(timesheets.arrivedAtTheResidence))
    ) {
      updatedTimesheets.leavingTheResidence = undefined;
      updatedTimesheets.arrivedBackToTheWarehouse = undefined;
    }

    if (
      timesheets.leavingTheResidence &&
      timesheets.arrivedBackToTheWarehouse &&
      isBefore(
        new Date(timesheets.arrivedBackToTheWarehouse),
        new Date(timesheets.leavingTheResidence)
      )
    ) {
      updatedTimesheets.arrivedBackToTheWarehouse = undefined;
    }

    if (Object.keys(updatedTimesheets).length) {
      setTimesheets((timesheets) => ({
        ...timesheets,
        ...updatedTimesheets,
      }));
    }
  }, [timesheets]);

  const arrivedAtTheResidenceChange = useMemo(() => {
    return timesheets?.arrivedAtTheResidence
      ? format(new Date(timesheets?.arrivedAtTheResidence), DATE_FORMAT)
      : "";
  }, [timesheets]);

  const breakFrom = useMemo(() => {
    return getDateTimeLabelText(timesheets?.breakFrom);
  }, [timesheets]);

  const backFromBreak = useMemo(() => {
    return getDateTimeLabelText(timesheets?.backFromBreak);
  }, [timesheets]);

  const leavingTheResidence = useMemo(() => {
    return getDateTimeLabelText(timesheets?.leavingTheResidence);
  }, [timesheets]);

  const arrivedBackToTheWarehouse = useMemo(() => {
    return getDateTimeLabelText(timesheets?.arrivedBackToTheWarehouse);
  }, [timesheets]);

  const createChangeHandler = (fieldName: keyof MemberTimesheet) => {
    return (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setTimesheets((prevTimesheets) => ({
        ...prevTimesheets,
        [fieldName]: event.target.value
          ? returnDateFormation(event.target.value).toISOString()
          : undefined,
      }));
    };
  };

  const handleArrivedAtTheResidenceChange = createChangeHandler("arrivedAtTheResidence");
  const handleBreakFromChange = createChangeHandler("breakFrom");
  const handleBackFromBreakChange = createChangeHandler("backFromBreak");
  const handleLeavingTheResidence = createChangeHandler("leavingTheResidence");
  const handleArrivedBackToTheWarehouse = createChangeHandler("arrivedBackToTheWarehouse");

  return (
    <TimesheetFormWrapper>
      <Grid container direction="column" spacing={2} xs={12}>
        <Grid item container xs={12} spacing={2}>
          <Grid item xs={12} md={6}>
            <Input
              label={translate("arrivedAtTheResidence")}
              type="datetime-local"
              disabled={isOnView || isLoading}
              required
              value={arrivedAtTheResidenceChange}
              max={breakFrom}
              onChange={handleArrivedAtTheResidenceChange}
              error={fieldHasError("arrivedAtTheResidence")}
              errorMessage={getErrorByField("arrivedAtTheResidence")}
            />
          </Grid>
        </Grid>
        <Grid item container xs={12} spacing={2}>
          <Grid item xs={12} md={6}>
            <Input
              label={translate("breakFrom")}
              type="datetime-local"
              disabled={isOnView || Boolean(!timesheets?.arrivedAtTheResidence) || isLoading}
              required={!!backFromBreak?.length}
              min={arrivedAtTheResidenceChange}
              value={breakFrom}
              max={backFromBreak}
              onChange={handleBreakFromChange}
              error={fieldHasError("breakFrom")}
              errorMessage={getErrorByField("breakFrom")}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Input
              label={translate("to")}
              type="datetime-local"
              disabled={isOnView || Boolean(!timesheets?.breakFrom) || isLoading}
              required={!!breakFrom?.length}
              value={backFromBreak}
              min={breakFrom}
              max={leavingTheResidence}
              onChange={handleBackFromBreakChange}
              error={fieldHasError("backFromBreak")}
              errorMessage={getErrorByField("backFromBreak")}
            />
          </Grid>
        </Grid>
        <Grid item container xs={12} spacing={2}>
          <Grid item xs={12} md={6}>
            <Input
              label={translate("leavingTheResidence")}
              type="datetime-local"
              disabled={isOnView || Boolean(!timesheets?.arrivedAtTheResidence) || isLoading}
              required
              value={leavingTheResidence}
              min={backFromBreak}
              max={arrivedBackToTheWarehouse}
              onChange={handleLeavingTheResidence}
              error={fieldHasError("leavingTheResidence")}
              errorMessage={getErrorByField("leavingTheResidence")}
            />
          </Grid>
        </Grid>
        <Grid item container xs={12} spacing={2}>
          <Grid item xs={12} md={6}>
            <Input
              label={translate("arrivedBackToTheWarehouse")}
              type="datetime-local"
              disabled={isOnView || Boolean(!timesheets?.leavingTheResidence) || isLoading}
              value={arrivedBackToTheWarehouse}
              min={leavingTheResidence}
              onChange={handleArrivedBackToTheWarehouse}
              error={fieldHasError("arrivedBackToTheWarehouse")}
              errorMessage={getErrorByField("arrivedBackToTheWarehouse")}
            />
          </Grid>
        </Grid>
      </Grid>
    </TimesheetFormWrapper>
  );
};
